import { createSelector, createReducer } from '@reduxjs/toolkit';
import { takeEvery, call, fork } from 'redux-saga/effects';
import { updateDtoList } from '../helpers/reducerHelpers';
import {
  getRequestData,
  selectAllFromApiState,
  getIncludedData,
} from '../helpers/selectorHelpers';
import { UserConnector } from '../connectors/userConnector';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import { USER_INCLUDE } from '../../constants/api';
import {
  deleteInvitationSourceMappingsSuccess,
  deleteInvitationSourceSuccess,
  getAllCommentsForHearingSuccess,
  getAllHearingsSuccess,
  getAllUsersInitialize,
  getAllUsersSuccess,
  getHearingSuccess,
  getInvitationNotificationsForHearingSuccess,
  getPaginatedCommentsForHearingSuccess,
  getPaginatedHearingsSuccess,
  uploadCsvInviteesSuccess,
  uploadExcelInviteesSuccess,
  uploadInvitationGroupInviteesSuccess,
  uploadPersonalInviteesSuccess,
} from '../actions/api';
import {
  allUserHearingRolesWithRoleByHearingIdSelector,
  userHearingRoleOwnerByHearingId,
  userHearingRoleOwnersByHearingIdsSelector,
} from './userHearingRoleSlice';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { all } from 'deepmerge';
import { userCapacityByCapacitySelector } from './userCapacitySlice';
// import { userHearingRoleOwnersByHearingIdsSelector } from './userHearingRoleSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /user
//  - GET /user/{id}
//
// User is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{id}
//  - GET /hearing/{hearingId}/comment

// -------- Reducer --------
const userReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllUsersSuccess, (state, action) => {
        const users = getRequestData(action);
        return updateDtoList(state, 'all', users);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(uploadCsvInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(uploadExcelInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(uploadPersonalInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(uploadInvitationGroupInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(deleteInvitationSourceSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(deleteInvitationSourceMappingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(getAllCommentsForHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(getPaginatedCommentsForHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      })
      .addCase(getInvitationNotificationsForHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const users = data.filter((include) => include.type === USER_INCLUDE);
        return updateDtoList(state, 'all', users);
      });
  },
);

// -------- Sagas --------
export function* userSaga() {
  yield all([fork(getAllUsersSaga)]);
}

export function* getAllUsersSaga() {
  yield takeEvery(
    getAllUsersInitialize.type,
    generateGetAllApiCall(UserConnector, function* (connector) {
      return yield call([connector, connector.getUsers]);
    }),
  );
}

// -------- Selectors --------
const selectGetAllUsers = (state) => selectAllFromApiState(state, 'user');

const selectGetUserById = (state, props) => {
  const users = selectAllFromApiState(state, 'user');
  return users.find((user) => user.id === props.userId);
};

const allUsersSelector = createSelector(selectGetAllUsers, (users) => users);

const allHearingCreatorsSelector = createSelector(selectGetAllUsers, (users) =>
  users.filter((user) => getAttr(user, 'isHearingCreator')),
);

const userByIdSelector = createSelector(selectGetUserById, (user) => user);

const allUsersInRoleByHearingIdSelector = (hearingRole, hearingId) =>
  createSelector(
    [
      allUsersSelector,
      allUserHearingRolesWithRoleByHearingIdSelector(hearingRole, hearingId),
    ],
    (users, userHearingRoles) =>
      users.filter((user) =>
        userHearingRoles.some(
          (userHearingRole) =>
            getRelId(userHearingRole, 'user') === getEntityId(user),
        ),
      ),
  );

const hearingOwnersByHearingIds = (hearingIds) =>
  createSelector(
    [selectGetAllUsers, userHearingRoleOwnersByHearingIdsSelector(hearingIds)],
    (users, userHearingRoleOwners) =>
      users.filter(
        (u) =>
          userHearingRoleOwners &&
          userHearingRoleOwners.some(
            (uhr) => getRelId(uhr, 'user') === getEntityId(u),
          ),
      ),
  );

const hearingOwnerByHearingId = (hearingId) =>
  createSelector(
    [selectGetAllUsers, userHearingRoleOwnerByHearingId(hearingId)],
    (users, userHearingRole) =>
      users.find(
        (user) => getRelId(userHearingRole, 'user') === getEntityId(user),
      ),
  );

const usersWithCapacitySelector = (capacity) =>
  createSelector(
    [allUsersSelector, userCapacityByCapacitySelector(capacity)],
    (users, userCapacity) =>
      users.filter(
        (user) => getRelId(user, 'userCapacity') === getEntityId(userCapacity),
      ),
  );

// -------- Exporting --------
export {
  allUsersSelector,
  userByIdSelector,
  hearingOwnersByHearingIds,
  hearingOwnerByHearingId,
  allHearingCreatorsSelector,
  usersWithCapacitySelector,
  allUsersInRoleByHearingIdSelector,
};
export default userReducer;
